module Meevo.Components.Common {
    /**
    * Contract to hold message names passed throughout the app via Angular.
    * Using static get() functions so that the members can't be changed by anyone
    * TODO: Move the rest of the string literals used for Angular messages to this class.
    */
    export class Messages {
        /**
        * Start the global busy indicator
        */
        public static get GLOBAL_BUSY_START(): string { return "GlobalBusyStart"; }

        /**
        * Stop the global busy indicator
        */
        public static get GLOBAL_BUSY_STOP(): string { return "GlobalBusyStop"; }

        /**
         * Change the global busy progress
         */
        public static get GLOBAL_BUSY_PROGRESS(): string { return 'GlobalBusyProgress'; }

        /**
        * Transition to another state.
        */
        public static get GOTO_STATE(): string { return "gotoState"; }

        /**
        * Ask a web-component-mount directive to re-evaluate its feature gate and
        * re-render the correct experience in place (no page reload). Payload:
        * { componentId: string }. Consumed by the webComponentMount directive.
        */
        public static get WEB_COMPONENT_REGATE(): string { return "webComponentRegate"; }

        /**
        * Emitted by the webComponentMount directive once a component has finished
        * mounting, so consumers can seed it. Payload:
        * { componentId: string, mode: 'enhanced' | 'legacy' }.
        */
        public static get WEB_COMPONENT_MOUNTED(): string { return "webComponentMounted"; }

        /**
        * Appointment book intent: the user chose the new experience from the
        * legacy banner. The AB run block captures the current legacy date/view,
        * seeds the incoming web component, then broadcasts WEB_COMPONENT_REGATE.
        */
        public static get APPOINTMENT_BOOK_SWITCH_TO_NEW(): string { return "appointmentBook:switchToNew"; }

        /**
        * The new-experience banner's rendered height changed (it appeared, was
        * dismissed, or reflowed). The legacy AB builder recomputes its header
        * offset and repositions the grid so hit-testing and layout stay in sync
        * with the space the banner occupies.
        */
        public static get APPOINTMENT_BOOK_RELAYOUT(): string { return "appointmentBook:relayout"; }

        /**
        * Closes the client search menu.
        */
        public static get CLOSE_SEARCH_MENU(): string { return "closeSearchMenu"; }

        /**
        * Opens the client search menu.
        */
        public static get OPEN_SEARCH_MENU(): string { return "openSearchMenu"; }

        /**
        * Closes the meeov ai chat.
        */
        public static get CLOSE_AICHAT(): string { return "closeAiChat"; }

        /**
        * Opens the meevo ai chat.
        */
        public static get OPEN_AICHAT(): string { return "openAiChat"; }

        /**
        * Opens the meevo ai chat.
        */
        public static get MINIMIZE_AICHAT(): string { return "minimizeAiChat"; }

        /**
        * Notifcation when the search menu has completed opening.
        */
        public static get OPEN_SEARCH_MENU_COMPLETE(): string { return "openSearchMenuComplete"; }

        /**
        * Notifcation when the search menu has completed closing.
        */
        public static get CLOSE_SEARCH_MENU_COMPLETE(): string { return "closeSearchMenuComplete"; }

        /**
        * Notifcation when the ai chat has completed opening.
        */
        public static get OPEN_AICHAT_COMPLETE(): string { return "openAiChatComplete"; }

        /**
        * Notifcation when the ai chat has completed closing.
        */
        public static get CLOSE_AICHAT_COMPLETE(): string { return "closeAiChatComplete"; }

        /**
        * Notifcation when the ai chat has completed opening.
        */
        public static get DETACH_AICHAT(): string { return "detachAiChat"; }

        /**
        * Notifcation when the ai chat restarts.
        */
        public static get RESTART_AICHAT(): string { return "restartAiChat"; }

        /**
        * Removes the client from the recent list.
        */
        public static get SEARCH_MENU_DELETE_CLIENT(): string { return "searchMenuDeleteClient"; }

        /**
        * Closes the context;
        */
        public static get CLOSE_CONTEXT(): string { return "closeContext"; }

        public static get CLOSE_REMOTE_CONTEXTS(): string { return "closeRemoteContexts"; }

        public static get CLOSE_CONTEXT_WARNING(): string { return "closeContextWarning"; }

        public static get CLOSE_DRAWERMANAGER_SECTION(): string { return "closeDrawerManagerSection"; }

        /**
        * Indicates a menu item was selected.
        */
        public static get MENU_ITEM_SELECTED(): string { return "menuItemSelected"; }

        /**
        * Indicates a global-nav submenu (L2) item was selected. Used to track the
        * last-selected L2 item so it can be shown in a selected state when the
        * enhanced global menu is reopened (AB#129215, rule 2.12).
        */
        public static get SUB_MENU_ITEM_SELECTED(): string { return "subMenuItemSelected"; }


        /**
        * Indicates a menu item is opened.
        */
        public static get MENU_ITEM_OPENED(): string { return "menuItemOpened"; }

        /**
        * Opens the main meevo menu.
        */
        public static get OPEN_MENU(): string { return "openMenu"; }

        /**
        * Closes the main meevo menu.
        */
        public static get CLOSE_MENU(): string { return "closeMenu"; }

        /**
        * Shows the meevo sub menu.
        */
        public static get SUB_MENU_SHOW(): string { return "subMenuShow"; }

        /**
        * Indicates that a context item has changed.
        */
        public static get CONTEXT_ITEM_CHANGED(): string { return "contextItemChanged"; }

        /**
        * Applies params to the current context.
        */
        public static get APPLY_PARAMS_TO_CONTEXT(): string { return "applyParamsToContext"; }

        /**
        * Applies params to the current context.
        */
        public static get MENU_ITEM_SELECTED_VIA_CONVO(): string { return "menuItemSelectedViaConvo"; }

        /**
        * Used to indicate when server validation occurs.
        */
        public static get SERVER_VALIDATION(): string { return "serverValidation"; }

        /**
        * Used to indicate when server validation has completed.
        */
        public static get SERVER_VALIDATION_COMPLETE(): string { return "serverValidationComplete"; }

        /**
        * Synchronize the convo with the current context.
        */
        public static get SYNCHRONIZE_CONVO(): string { return "synchronizeConvo"; }

        /**
         * Triggered when UI-Router state change starts.
         */
        public static get STATE_CHANGE_START(): string { return "$stateChangeStart"; }

        /**
         * Triggered when UI-Router state change succeeds
         */
        public static get STATE_CHANGE_SUCCESS(): string { return "$stateChangeSuccess"; }

        /**
        * Indicates the authentication was successful.
        */
        public static get AUTHENTICATION_SUCCESS(): string { return "authenticationSuccess"; }

        /**
        * Used to indicate when appointments are checked in via client find.
        */
        public static get CLIENT_FIND_APPT_CHECKED_IN(): string { return "clientFindApptCheckedIn"; }

        /**
        * Used to indicate when appointments are checked out via client find.
        */
        public static get CLIENT_FIND_APPT_CHECKED_OUT(): string { return "clientFindApptCheckedOut"; }

        /**
        * Used to indicate that a convo has been requested.
        */
        public static get CONVO_REQUESTED(): string { return "convoRequested"; }

        /**
        * Triggered when a client is selected anywhere in the system that is being tracked
        */
        public static get CLIENT_SELECTED(): string { return "clientSelected" };

        public static get RDS_RESPONSE(): string { return "RdsResponse" };

        public static get PENDING_MESSAGES(): string { return "pendingMessages"; }

        public static get DRAWER_CLOSED_BY_ANOTHER_WARNING(): string { return "drawerClosedByAnother"; }

        public static get NO_SHOW(): string { return "noShow"; }

        public static get DRAWER_STATE_CHANGED(): string { return "DrawerStateChanged"; }

        public static get REFRESH_DRAWER_MANAGER(): string { return "refreshDrawerManager"; }

        public static get DRAWER_MANAGER_SECTION_SWITCHED(): string { return "drawerManagerSectionSwitched"; }

        public static get POS_HARDWARE_UPDATED(): string { return "posHardwareUpdated"; }

        public static get SET_CURRENT_DRAWER(): string { return "SetCurrentDrawer"; }

        public static get REGISTER_DRAWER_SELECTED(): string { return "drawerSelected"; }

        public static get PRINTER_SELECTED(): string { return "printerSelected"; }

        public static get TERMINAL_SELECTED(): string { return "terminalSelected"; }

        public static get DRAWER_TERMINAL_UPDATE(): string { return "drawerTerminalUpdate"; }

        public static get REGISTER_TERMINAL_UPDATE(): string { return "registerTerminalUpdate"; }

        public static get DRAWER_OPENED(): string { return "drawerOpened"; }

        public static get DRAWER_CLOSED(): string { return "drawerClosed"; }

        public static get RETURN_FROM_ON_HOLD(): string { return "ReturnFromOnHold"; }

        public static get PACKAGE_TRANSFER(): string { return "PackageTransfer"; }

        public static get CHECKOUT_APPT_SERVICES(): string { return "CheckOutAppointmentServices"; }

        public static get ELEVATION_CHALLENGE(): string { return "ElevationChallenge"; }

        public static get MEEVO_FIELD_LABELS_SET(): string { return "meevoFieldlabelsSet"; }

        /**
         * Used to hide modal dialog window
         */
        public static get HIDE_MODAL_WINDOW(): string { return "hideModal"; }

        /**
        * Transfer to register with refund receipt for given transaction number
        */
        public static get REGISTER_REFUND_RECEIPT(): string { return "RegisterRefundReceipt"; }

        /**
        * Transfer to register for Manual Payment
        */
        public static get MANUAL_BILL_PAYMENT(): string { return "ManualBillPayment"; }

        public static get MANUAL_PAYMENT_PLAN_BILL_PAYMENT(): string { return "ManualPaymentPlanBillPayment"; }

        public static get MANUAL_BILL_PAYMENT_REFUND(): string { return "ManualBillPaymentRefund"; }

        public static get MANUAL_SALE_LINE(): string { return "ManualSaleLine"; }

        public static get MANUAL_SALE_LINES(): string { return "ManualSaleLines"; }

        /**
        * Book appointment for a client in the appointment book
        */
        public static get BOOK_APPOINTMENT_FOR_CLIENT(): string { return "BookAppointmentForClient"; }

        /**
        * Ring up transaction for a client in the register
        */
        public static get RING_UP_TRANSACTIONFOR_CLIENT(): string { return "RingUpTransactionForClient"; }

        /**
        * Transfer to register with client gift card data
        */
        public static get REGISTER_RELOAD_CLIENT_GIFTCARD(): string { return "RegisterReloadClientGiftCard"; }

        public static get APPOINTMENT_SUGGESTIONS_CHANGED(): string { return "appointmentSuggestionsChanged"; }

        public static get SUGGESTIONS_CLIENT_ADDEDIT_CALLBACK(): string { return "suggestionsClientAddEditCallback"; }

        public static get APPOINTMENT_BOOK_SUGGESTION(): string { return "appointmentBookedViaSuggestion"; }

        public static get APPOINTMENT_BOOK_PROCESS_SUGGESTION(): string { return "appointmentBookProcessSuggestion"; }

        public static get APPOINTMENT_NEW_APPOINTMENT(): string { return "newAppointment"; }

        public static get TYPEAHEAD_FOCUS(): string { return 'typeaheadFocus'; }

        public static get SESSION_COUNTDOWN(): string { return 'sessionCountdown'; }

        public static get CLEAR_SESSION_TIMEOUT(): string { return 'ClearSessionTimeout'; }

        public static get SESSION_TIMEOUT_HANDLER(): string { return 'SessionTimeoutHandler'; }

        public static get SESSION_EXPIRING(): string { return 'sessionExpiring'; }

        public static get SESSION_LOCKOUT(): string { return 'sessionLockout'; }

        public static get REFRESH_SECURITY_KEYS(): string { return 'refreshSecurityKeys'; }

        public static get REFRESH_IDENTITY(): string { return 'refreshIdentity'; }

        public static get CONTEXT_HAS_CLOSED(): string { return 'contextHasClosed' };

        public static get MENU_SHORT_CUT_CLICK(): string { return "menuSortCutClick"; }

        public static get SUB_MENU_SHORT_CUT_CLICK(): string { return "subMenuSortCutClick"; }

        public static get REFRESH_LOCKOUT_LAST_EVENT_TIME(): string { return "refreshLockoutLastEventTime"; }

        /**
        * Indicates a message for MeevoMessenger was received
        */
        public static get MEEVO_MESSENGER_INBOUND_MESSAGE(): string { return "meevoMessengerInboundMessage"; }

        public static get TEXTEL_MESSAGE_STATUS(): string { return "textelMessageStatus"; }

        public static get MEEVO_MESSENGER_PROCESS_INBOUND_MESSAGE(): string { return "textelProcessInboundMessage"; }

        public static get MEEVO_MESSENGER_UPDATE_USER_TO_NOTIFY(): string { return "meevoMessengerUpdateUserToNotify"; }

        public static get MEEVO_MESSENGER_REDUCE_BADGE_NUMBER(): string { return "meevoMessengerReduceBadgeNumber"; }

        public static get TEXTEL_UPDATE_SYSTEM_AUTO_REPLY_TIMER(): string { return "textelupdateSystemAutoReplyTimer"; }

        public static get TEXTEL_UPDATE_MODEL(): string { return "textelUpdateModel"; }

        public static get TEXTEL_SETTINGS_UPDATE_MODEL(): string { return "textelSettingsUpdateModel"; }

        public static get TWO_WAY_SMS_MESSAGE_STATUS(): string { return "TWO_WAY_SMS_MESSAGE_STATUS"; } //change return value

        public static get CLOSE_INTAKE_FORM(): string { return "closeIntakeForm"; }

        public static get CLOSE_DAF_FORM(): string { return "closeDafForm"; }

        public static get CLOSE_KIOSK_MODE(): string { return "closeKioskMode"; }

        public static get CLOSE_KIOSK_MODE_OUTSIDE(): string { return 'closeKioskModeOutside'; }

        public static get ENABLE_LOGOUT_AFTER_RELOAD(): string { return 'enableLogoutAfterReload'; }

        public static get CHECK_SECURITY_KEY_AFTER_ELEVATION(): string { return 'checkSecurityKeyAfterElevation'; }

        /**
        * Update open register sessions with new tip settings
        */
        public static get ENABLE_TIPS_SETTING_UPDATED(): string { return "EnableTipsSettingUpdated"; }

        public static get FORCE_SESSION_RELOAD(): string { return "forceSessionReload"; }

        /**
         * Update navbar location when Call Center emulation changes
         */
        public static get UPDATE_LOCATION_NAME(): string { return "updateLocationName"; }

        public static get CALL_CENTER_EMULATION_CHANGE(): string { return "callCenterEmulationChange"; }

        public static get CLOSE_FORM_DESIGNER(): string { return "CloseFormDesigner"; }

        public static get CLOSE_INTAKE_FORM_DESIGNER(): string { return "CloseIntakeFormDesigner"; }

        public static get RESTART_SESSION_COUNTDOWN(): string { return "restartSessionCountdown"; }

        public static get KEEP_SESSION_ALIVE(): string { return 'KeepSessionAlive'; }

        /**
         * Past Transaction
         */

        public static get SET_PAST_TRANSACTION_DRAWER(): string { return "SetPastTransactionDrawer"; }

        public static get TRANSACTION_EDITOR_INSERT(): string { return "TransactionEditorInsert"; }

        public static get NO_SALE_SELF_PAYMENT_ENTITY_VERSION_VALIDATION_FAILURE(): string { return "NoSaleSelfPaymentEntityVersionValidationFailure"; }

        /**
         * Inventory
         */
        public static get PRODUCT_SAVED(): string { return "productSaved"; }

        /**
         * Indicators
         */
        public static get RESET_INDICATORS(): string { return "maintenanceItemSaved"; }

        /**
         * Interop
         */
        public static get RETURN_FROM_WEB_MEEVO(): string { return "ReturnFromWebMeevo"; }

        public static get ADD_PAYMENT_REQUEST_TO_SALE(): string { return "AddPaymentRequestToSale" };

        public static get TIPPY_TIPS_SELECTED(): string { return "TippyTipsSelected" };

        public static get PAYMENT_PLAN_PAYMENT_FINALIZED(): string { return "mi:PaymentPlanPaymentFinalized"; };

        public static get SET_NEW_COLOR_THEME(): string { return "mi:SetNewColorTheme"; };

        public static get SET_MAINTENANCE_FORM_STATE(): string { return "SetMaintenanceFormState"; };
        /**
         * Analytics tracking
         */
        public static get ANALYTICS_TRACK_CONTROLLER(): string { return "AnalyticsTrackController" };

        public static get ANALYTICS_CLEAR_SECTION(): string { return "AnalyticsClearSecton " };

        public static get AUTO_LOGOUT_USER(): string { return "AutoLogOutUser"; }

        public static get EXTERNAL_ORDERING_AUTH_OBTAINED(): string { return "ExternalOrderingAuthObtained"; }
        public static get EXTERNAL_ORDERING_AUTH_CODE_OBTAINED(): string { return "ExternalOrderingCodeObtained"; }

        public static get EXTERNAL_ORDERING_ACCESS_TOKEN_OBTAINED(): string { return "ExternalOrderingAccessTokenObtained"; }

        public static get SEND_MESSAGE(): string { return "SendMessage" };

        // Fired after the tenant/location business-info settings have been re-pulled
        // live (e.g. a location-level toggle saved from a Meevo shell v2 settings
        // screen). Lets settings-bound UI re-evaluate without a logout/login cycle.
        public static get BUSINESS_INFO_SETTINGS_REFRESHED(): string { return "BusinessInfoSettingsRefreshed" };
    }
}
